import { EventEmitter, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  AlertRules,
  ServiceOrder,
} from '../components/alerts/models/alerts.model';
import { ErrorLibService } from './error-lib.service';
import { BehaviorSubject, take, tap } from 'rxjs';
import {
  CHANGE_ALERT_STATUS,
  FILTER_ALERTS,
  GET_ALERT_BY_ID,
  GET_EQUIPMENT_ALERT,
  FILTER_UNIFIED_ALERTS
} from 'src/app/graphql/alerts.graphql';
import {
  ADD_SERVICE_ORDER_TO_ALERT,
  CREATE_SERVICE_ORDER,
  FILTER_SERVICE_ORDER,
  GET_SERVICE_ORDER_INFO,
} from 'src/app/graphql/service-order.queries';
import { GET_ALERT_RULES } from 'src/app/graphql/alert-rules.graphql';
import { GET_INSTALLATION_SERIAL } from 'src/app/graphql/installations.graphql';
import { FILTER_GATEWAYS } from 'src/app/graphql/gateways.graphql';

@Injectable({
  providedIn: 'root',
})

export class AlertsService {
  company: string | null = '';
  constructor(
    private apollo: Apollo,
    private errorLib: ErrorLibService,
  ) {
    this.company = localStorage.getItem('lastCompanySelected');
  }

  //Para pegar os dados da tabela é necessário criar uma variável behaviorSubject e uma variável observable
  //BehaviorSubject é um tipo de observable que permite que você emita um valor inicial antes que qualquer outro observador se inscreva nele.
  //Observable é um tipo de dado que permite que você receba notificações quando um valor é emitido.

  //Para controlar o processor de loading dos dados da tabela usasse uma variável behaviorSubject e uma variável observable da mesma forma

  loadingAlertDataBehaviorSubject = new BehaviorSubject<boolean>(false);
  loadingAlertData$ = this.loadingAlertDataBehaviorSubject.asObservable();

  serviceOrdersBehaviorSubject = new BehaviorSubject<ServiceOrder[]>([]);
  serviceOrders$ = this.serviceOrdersBehaviorSubject.asObservable();

  serviceOrdersLoadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  serviceOrdersLoading$ =
    this.serviceOrdersLoadingBehaviorSubject.asObservable();

  loadingAlertRulesBehaviorSubject = new BehaviorSubject<boolean>(false);
  loadingAlertRules$ = this.loadingAlertRulesBehaviorSubject.asObservable();

  alertsRulesBehaviorSubject = new BehaviorSubject<AlertRules[]>([]);
  alertRules$ = this.alertsRulesBehaviorSubject.asObservable();

  public loggedUser = localStorage.getItem('userId');

  //Para controlar a paginação da tabela é necessário criar uma variável pagination
  paginatorEventEmitter = new EventEmitter();
  filterPagination = { cursor: null, First: 20, end: false, locked: false };
  alertRulesPagination = { cursor: null, First: 20, end: false, locked: false };

  ruleStatus = {
    NEW: 'novo',
    AWARE: 'Inativo',
    OPENEDSO: 'Aberto O.S',
    RESOLVED: 'Resolvido',
  };

  public filterAlerts(
    installationReference: string | null,
    firstAlertDateTime: string | null,
    lastAlertDateTime: string | null,
    alertCode: [any] | null,
    alertState: string | null,
    limit: number | null = null,
    offset: number | null = null,
  ) {
    let firstAlert = firstAlertDateTime
      ? new Date(firstAlertDateTime).toISOString().slice(0, 19)
      : null;

    let lastAlert = lastAlertDateTime
      ? new Date(lastAlertDateTime).toISOString().slice(0, 19)
      : null;

    return this.apollo.watchQuery({
      query: FILTER_UNIFIED_ALERTS,
      fetchPolicy: 'no-cache',
      variables: {
        company: this.company,
        installationReference: installationReference,
        firstAlertDateTime: firstAlert,
        lastAlertDateTime: lastAlert,
        alertCode_In: alertCode,
        alertState,
        limit,
        offset,
      },
    });
  }


  public getEquipmentAlerts(
    datetimeGte: string | null,
    datetimeLte: string | null,
    route: string | null = null,
    installations: string | null = null,
    cursor: string | null = null,
    pageSize: number | null = null,
  ) {
    return this.apollo.watchQuery({
      query: GET_EQUIPMENT_ALERT,
      fetchPolicy: 'no-cache',
      variables: {
        company: this.company,
        datetimeGte,
        datetimeLte,
        route,
        installations,
        cursor,
        pageSize,
      },
    });
  }

  public getInstallationListByReference(reference: string | null) {
    return this.apollo.watchQuery({
      query: GET_INSTALLATION_SERIAL,
      fetchPolicy: 'no-cache',
      variables: {
        company: this.company,
        referenceContains: reference,
      },
    });
  }

  public getGatewaysByReference(reference: string) {
    return this.apollo.watchQuery({
      query: FILTER_GATEWAYS,
      variables: {
        reference,
        company: this.company,
        sort_dir: 'ASC',
        sort_field: 'REFERENCE',
      },
    });
  }

  public getAlertById(id: string) {
    this.loadingAlertDataBehaviorSubject.next(true);
    return this.apollo
      .watchQuery({
        query: GET_ALERT_BY_ID,
        fetchPolicy: 'network-only',
        variables: {
          id,
          company: this.company,
        },
      })
      .valueChanges.pipe(
        take(1),
        tap((res: any) => {
          this.loadingAlertDataBehaviorSubject.next(false);
        }),
      );
  }

  public createServiceOrder(
    reference: any,
    assignee: any,
    chAlertId: any,
    installationId: any,
    installationReference: any,
    status: any,
    priority: any,
    latitude: any,
    longitude: any,
    deviceMac: any,
  ) {
    return this.apollo.mutate({
      mutation: CREATE_SERVICE_ORDER,
      variables: {
        company: this.company,
        reference,
        assignee,
        chAlertId,
        installationId,
        installationReference,
        status,
        priority,
        latitude,
        longitude,
        deviceMac,
      },
    });
  }

  public getServiceOrder(reference: string | null) {
    this.serviceOrdersLoadingBehaviorSubject.next(true);
    return this.apollo
      .watchQuery({
        query: FILTER_SERVICE_ORDER,
        fetchPolicy: 'network-only',
        variables: {
          reference,
          company: this.company,
          direction: 'DESC',
          field: 'CREATION_DATE',
        },
      })
      .valueChanges.pipe(
        take(1),
        tap((res: any) => {
          let serviceOrderList: ServiceOrder[] = [];
          res?.data?.serviceOrder?.edges.forEach((serviceOrder: any) => {
            if (serviceOrder?.node?.status != 'CLOSED') {
              serviceOrderList.push(
                new ServiceOrder(
                  serviceOrder?.node?.id,
                  serviceOrder?.node?.reference,
                  serviceOrder?.node?.chAlert,
                  serviceOrder?.node?.createdAt,
                  serviceOrder?.node?.status,
                ),
              );
            }
          });
          this.serviceOrdersBehaviorSubject.next(serviceOrderList);
          this.serviceOrdersLoadingBehaviorSubject.next(false);
        }),
      );
  }

  public getServiceOrderOnAlertInformation(alerts: string) {
    return this.apollo.watchQuery({
      query: GET_SERVICE_ORDER_INFO,
      fetchPolicy: 'network-only',
      variables: {
        company: this.company,
        alerts: [alerts],
      },
    });
  }

  public getServiceOrderInformation(reference: string) {
    return this.apollo.watchQuery({
      query: GET_SERVICE_ORDER_INFO,
      fetchPolicy: 'network-only',
      variables: {
        company: this.company,
        reference,
      },
    });
  }

  public addServiceOrderToAlert(alertId: string, serviceOrderId: string) {
    return this.apollo.mutate({
      mutation: ADD_SERVICE_ORDER_TO_ALERT,
      variables: {
        id: serviceOrderId,
        clientMutationId: this.loggedUser,
      },
    });
  }

  public changeAlertStatus(ids: string[], status: string) {
    return this.apollo.mutate({
      mutation: CHANGE_ALERT_STATUS,
      variables: {
        ids,
        status,
      },
    });
  }

  public resetPaginations() {
    this.filterPagination = {
      cursor: null,
      First: 20,
      end: false,
      locked: false,
    };
    this.alertRulesPagination = {
      cursor: null,
      First: 20,
      end: false,
      locked: false,
    };
  }
}
